package ui

import enums.CommanderTrait
import enums.CommanderType
import kotlinx.coroutines.await
import globals.AccelByteEnv
import globals.KEnv
import globals.World
import io.kvision.core.AlignItems
import io.kvision.core.Background
import io.kvision.core.Border
import io.kvision.core.BorderStyle
import io.kvision.core.BoxShadow
import io.kvision.core.Col
import io.kvision.core.Color
import io.kvision.core.Display
import io.kvision.core.FlexDirection
import io.kvision.core.FontWeight
import io.kvision.core.JustifyContent
import io.kvision.core.JustifyItems
import io.kvision.core.Overflow
import io.kvision.core.Position
import io.kvision.core.TextAlign
import io.kvision.core.onClick
import io.kvision.core.onInput
import io.kvision.form.text.TextInput
import io.kvision.form.text.textInput
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.h2
import io.kvision.html.h3
import io.kvision.html.icon
import io.kvision.html.p
import io.kvision.html.span
import io.kvision.panel.SimplePanel
import io.kvision.panel.hPanel
import io.kvision.panel.vPanel
import io.kvision.utils.perc
import io.kvision.utils.px
import kotlinx.browser.document
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.ttt.autogenesis.kvisionapp.ServerExtendBridge
import org.ttt.autogenesis.logging.LogCategory
import org.ttt.autogenesis.logging.Logger
import org.ttt.autogenesis.network.RpcJson
import structs.accelbyte.cloudsave.CloudPlayerMaps
import structs.rpcRequests.ListPlayerMapsRequest
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import structs.Commander
import structs.requests.CommanderCreateRequest

private enum class CollectionPage
{
    COMMANDERS,
    STORIES,
    MAPS
}

private data class CommanderDisplayInfo(
    val name: String,
    val description: String,
    val empire: String,
    val type: CommanderType,
    val trait: CommanderTrait,
    val iconClass: String,
    val source: Commander
)

private data class StoryEntry(
    val title: String,
    val dateCreated: String,
    val summary: String,
    val body: String
)

internal data class MapDisplayInfo(
    val id: String,
    val name: String,
    val subtitle: String,
    val status: String,        // "STOCK" | "PENDING REVIEW" | "READY"
    val isUserUpload: Boolean,
    val iconClass: String,
    val author: String,
    val playerCount: String,
    val territoryCount: String,
    val schemaVersion: String,
    val packSha: String
)

/**
 * Overlay that hosts the player's collection of commanders/stories. The widget is centered over the whole
 * viewport and keeps the same visual language as the other stat/detail dialogs.
 */
class CollectionOverlay : SimplePanel(className = "collection-overlay")
{
    private val commanderDetailWindow = CommanderDetailWindow()
    private val storyDetailWindow = StoryDetailWindow()
    private val mapUploadModal = MapUploadModal().also {
        // Register the success/error callbacks the server-extend notification
        // handlers route through. Decoupling here keeps CollectionOverlay out
        // of MapUploadModal's import surface and vice versa.
        MapUploadModal.successCallback = { mapName ->
            it.dismiss()
            // Bug fix (2026-08-14): the success MessageBox previously had
            // NO dismiss control — only an auto-hide timer at 3.5s. A human
            // reviewing the screenshot saw the title + body text but
            // could not click anything to dismiss the modal. Fix: pass
            // `showOk = true` and assign `onConfirm` after construction
            // (the constructor's `onConfirm` parameter is a `var`, so
            // reassignment is supported). The auto-hide timer remains as
            // a fallback for users who walk away from the keyboard, but
            // the user now has a primary dismiss control.
            val success = MessageBox(
                boxTitle = "Map uploaded",
                message = "Map '$mapName' uploaded",
                showOk = true
            )
            success.onConfirm = { success.hide() }
            KEnv.mainRoot?.add(success)
            // Auto-hide after 3.5s. MessageBox has no built-in timer;
            // use kotlinx.coroutines.delay on the MainScope.
            MainScope().launch {
                kotlinx.coroutines.delay(3500)
                success.hide()
            }
            refreshMapCatalogue()
        }
        MapUploadModal.errorCallback = { reason ->
            it.dismiss()
            val err = MessageBox(
                boxTitle = "Upload failed",
                message = "Upload failed: $reason",
                showOk = true
            )
            err.onConfirm = { err.hide() }
            KEnv.mainRoot?.add(err)
        }
        MapUploadModal.deleteSuccessCallback = { mapId, mapName ->
            mapDetailWindow.hide()
            // Remove the deleted card from the maps tab immediately so the
            // UI reflects the deletion without waiting for the next
            // refreshMapCatalogue() round-trip. The follow-up refresh calls
            // reconcile the source-of-truth against the local optimistic
            // removal (handles edge cases like the seed-upload probe path
            // where the catalogue was wiped before the delete RPC landed).
            removeMapCardById(mapId)
            refreshMapCatalogue()
            val success = MessageBox(
                boxTitle = "Map deleted",
                message = "Map '$mapName' deleted",
                showOk = true
            )
            success.onConfirm = { success.hide() }
            KEnv.mainRoot?.add(success)
            MainScope().launch {
                kotlinx.coroutines.delay(3500)
                success.hide()
            }
        }
        MapUploadModal.deleteErrorCallback = { reason ->
            mapDetailWindow.hide()
            val err = MessageBox(
                boxTitle = "Delete failed",
                message = "Delete failed: $reason",
                showOk = true
            )
            err.onConfirm = { err.hide() }
            KEnv.mainRoot?.add(err)
        }
    }
    private val mapDetailWindow = MapDetailWindow()
    private val storyEntries = listOf(
        StoryEntry(
            title = "Emberfall Siege",
            dateCreated = "December 04, 2025",
            summary = "A desperate stand under the Emberfall Rift where every commander learned what it means to lose control.",
            body = "December 4, 2025 - Emery, a young field marshal, ignored the wards and ordered his archers into the haunting glow..."
        ),
        StoryEntry(
            title = "Crown of Tidal Spires",
            dateCreated = "November 27, 2025",
            summary = "An aquatic alliance threatens to drown the outer provinces unless strange negotiations take hold.",
            body = "November 27, 2025 - The tide rose before the diplomats even reached the table. Admiral Kairu watched from the balcony..."
        ),
        StoryEntry(
            title = "Skybound Accord",
            dateCreated = "October 21, 2025",
            summary = "A fleet of flying citadels negotiates peace, but a warlord still brews in the lowlands.",
            body = "October 21, 2025 - The air itself hummed with tension. High Admiral Vela surveyed the crowd of envoys..."
        )
    )

    private var activePage = CollectionPage.COMMANDERS
    private lateinit var commanderTabButton: io.kvision.html.Button
    private lateinit var storyTabButton: io.kvision.html.Button
    private lateinit var searchField: TextInput
    private val commanderListContainer = vPanel {
        width = 100.perc
        flexGrow = 1
        overflow = Overflow.AUTO
        alignItems = AlignItems.CENTER
        spacing = 12
    }

    private val noCommanderMatches = p("No commanders match the current filter.") {
        width = 80.perc
        fontSize = 18.px
        textAlign = TextAlign.CENTER
        color = Color.name(Col.LIGHTGRAY)
        marginTop = 0.px
        marginBottom = 0.px
        visible = false
    }

    private data class CommanderCardEntry(val name: String, val panel: SimplePanel)

    private val commanderCards = mutableListOf<CommanderCardEntry>()

    private lateinit var mapsTabButton: io.kvision.html.Button
    private lateinit var mapsSearchField: io.kvision.form.text.TextInput
    private val mapsListContainer = vPanel(className = "collection-grid") {
        width = 100.perc
        flexGrow = 1
        overflow = Overflow.AUTO
        alignItems = AlignItems.STRETCH
        spacing = 12
    }
    private val mapsPage = vPanel(spacing = 16) {
        width = 100.perc
        flexGrow = 1
        overflow = Overflow.HIDDEN
        add(buildMapsSearchRow())
        add(mapsListContainer)
    }
    private val noMapMatches = p("No maps match the current filter.") {
        width = 80.perc
        fontSize = 18.px
        textAlign = TextAlign.CENTER
        color = Color.name(Col.LIGHTGRAY)
        marginTop = 0.px
        marginBottom = 0.px
        visible = false
    }
    private val mapCards = mutableListOf<MapCardEntry>()
    private data class MapCardEntry(
        val name: String,
        val panel: SimplePanel,
        val thumbnailRenderer: MapCardThumbnailRenderer?
    )

    private val storyListContainer = vPanel {
        width = 100.perc
        flexGrow = 1
        overflow = Overflow.AUTO
        alignItems = AlignItems.CENTER
        spacing = 16
    }

    private val commanderPage = vPanel(spacing = 16) {
        width = 100.perc
        flexGrow = 1
        overflow = Overflow.HIDDEN
        add(buildSearchRow())
        add(commanderListContainer)
    }

    private val storyPage = vPanel(spacing = 16) {
        width = 100.perc
        flexGrow = 1
        overflow = Overflow.HIDDEN
        add(p("Stories you have unlocked so far. Content playback still uses placeholder text until the editor content arrives.") {
            color = Color.name(Col.LIGHTGRAY)
            fontSize = 16.px
            textAlign = TextAlign.CENTER
            width = 100.perc
            marginTop = 0.px
            marginBottom = 0.px
        })
        add(storyListContainer)
    }

    init
    {
        setAttribute("data-testid", "collection-overlay")
        width = 100.perc
        height = 100.perc
        position = Position.FIXED
        top = 0.px
        left = 0.px
        zIndex = 8500
        background = Background(Color("rgba(0, 0, 0, 0.85)"))
        display = Display.NONE
        justifyContent = JustifyContent.CENTER
        alignItems = AlignItems.CENTER

        vPanel(
            className = "collection-window",
            spacing = 10,
            alignItems = AlignItems.CENTER
        ) {
            width = 92.perc
            height = 90.perc
            borderRadius = 22.px
            padding = 20.px
            background = Background(Color("rgba(7, 10, 24, 0.95)"))
            border = Border(2.px, BorderStyle.SOLID, Color("rgba(94, 106, 220, 0.4)"))
            boxShadow = BoxShadow(0.px, 35.px, 60.px, color = Color("rgba(0, 0, 0, 0.8)"))
            flexDirection = FlexDirection.COLUMN
            position = Position.RELATIVE

            hPanel(
                justify = JustifyContent.FLEXSTART,
                alignItems = AlignItems.CENTER
            ) {
                h2("Collection") {
                    color = Color.name(Col.WHITE)
                    marginTop = 0.px
                    marginBottom = 0.px
                    fontWeight = FontWeight.BOLD
                }
            }

            button("", icon = "fas fa-times", className = "btn btn-play btn-close-collection") {
                width = 60.px
                height = 60.px
                onClick {
                    this@CollectionOverlay.hide()
                }
                position = Position.ABSOLUTE
                top = (2).px
                right = 6.px
                zIndex = 50
            }

            hPanel(
                className = "collection-content",
                spacing = 0,
                alignItems = AlignItems.STRETCH
            ) {
                width = 100.perc
                height = 100.perc
                flexGrow = 1
                vPanel {
                    width = 100.perc
                    flexGrow = 1
                    overflow = Overflow.HIDDEN
                    flexDirection = FlexDirection.COLUMN
                    add(commanderPage)
                    add(storyPage)
                    add(mapsPage)
                }

                vPanel(
                    className = "collection-tab-strip",
                    spacing = 10,
                    alignItems = AlignItems.CENTER,
                    justify = JustifyContent.CENTER
                ) {
                    width = 90.px
                    height = 100.perc
                    commanderTabButton = button("", icon = "fas fa-user-astronaut", className = "collection-tab-button") {
                        title = "Commanders"
                        onClick { switchPage(CollectionPage.COMMANDERS) }
                    }
                    storyTabButton = button("", icon = "fas fa-book-open", className = "collection-tab-button") {
                        title = "Stories"
                        onClick { switchPage(CollectionPage.STORIES) }
                    }
                    mapsTabButton = button("", icon = "fas fa-map", className = "collection-tab-button") {
                        title = "Maps"
                        onClick { switchPage(CollectionPage.MAPS) }
                    }
                }
            }
        }

        add(commanderDetailWindow)
        add(storyDetailWindow)
        add(mapUploadModal)
        add(mapDetailWindow)

        // Note: The mobile-portrait CSS rules live in night-mode.css under the
        // `@media (max-width: 600px)` block at the end of the file. We do not
        // mirror them via KVision's Style.create DSL here because the
        // io.kvision.core.Style class in kvision-js 9.1.1 does not expose a
        // static `create` method to the Kotlin/JS target (the documented API
        // path is on a separate kvision sub-module not present in the JS-only
        // runtime). The CSS file approach is the load-bearing source of truth
        // and is loaded by the browser before this widget mounts.

        // Live rotation handling: when the viewport width crosses the 600px
        // breakpoint (typically a phone rotation), update the data-mobile-layout
        // attribute on the root so e2e probes can verify the layout state and
        // any future code that needs to react to layout change has a stable hook.
        kotlinx.coroutines.GlobalScope.launch {
            try {
                val mediaQuery = kotlinx.browser.window.matchMedia("(max-width: 600px), (max-height: 600px) and (orientation: portrait)")
                val updateAttribute: (Boolean) -> Unit = { matches ->
                    setAttribute("data-mobile-layout", if (matches) "portrait" else "desktop")
                }
                updateAttribute(mediaQuery.matches)
                mediaQuery.addEventListener("change", { event ->
                    val mql = event.asDynamic()
                    updateAttribute(mql.matches as Boolean)
                })
                Logger.info(LogCategory.UI, "CollectionOverlay: matchMedia listener attached, current state=${if (mediaQuery.matches) "portrait" else "desktop"}")
            }
            catch (err: Throwable) {
                Logger.warn(LogCategory.UI, "CollectionOverlay: matchMedia listener failed to attach (non-fatal): ${err.message}")
            }
        }
    }

    /**
     * Displays the overlay and refreshes its contents.
     */
    override fun show()
    {
        // Ensure saved commanders are loaded before rendering.
        // During normal login this is handled by the login flow, but skipLogin bypasses
        // that path so we load on-demand here.
        MainScope().launch {
            val loadingBox = MessageBox(boxTitle = "Loading saved commanders...")
            KEnv.mainRoot?.add(loadingBox)
            try {
                loadSavedCommanders(loadingBox)
            } finally {
                loadingBox.hide()
            }
            rebuildCommanderCards()
            renderStoryList()
            // Initial fetch of the player's uploaded maps. The re-fetch on
            // Map.Upload.Success happens via the success handler calling
            // CollectionOverlay.refreshMapCatalogue().
            refreshMapCatalogue()
            setActivePage(CollectionPage.COMMANDERS)
        }
        display = Display.FLEX
        visible = true
    }

    /**
     * Public hook invoked by the map-upload success flow. Re-fetches the
     * per-user map catalogue from server-extend and rebuilds the maps tab.
     * The userId comes from AccelByteEnv so the call scopes to the
     * authenticated player.
     */
    fun refreshMapCatalogue()
    {
        Logger.info(LogCategory.UI, "CollectionOverlay: refreshMapCatalogue() invoked")
        MainScope().launch {
            val invoker = ServerExtendBridge.rpcInvoker
            if (invoker == null)
            {
                Logger.warn(LogCategory.UI, "CollectionOverlay: refreshMapCatalogue — rpcInvoker is null (disconnected); skipping")
                return@launch
            }
            try
            {
                val userId = AccelByteEnv.userId
                val response = invoker.invoke(
                    "server.extend.listPlayerMaps",
                    ListPlayerMapsRequest(userId = userId),
                    ListPlayerMapsRequest.serializer()
                )
                if (response.error != null)
                {
                    Logger.warn(
                        LogCategory.UI,
                        "CollectionOverlay: refreshMapCatalogue — RPC error: ${response.error?.message ?: response.error?.code}"
                    )
                    return@launch
                }
                val payload = response.result
                    ?: run {
                        Logger.warn(LogCategory.UI, "CollectionOverlay: refreshMapCatalogue — null result payload")
                        return@launch
                    }
                val catalogue = RpcJson.decodeFromJsonElement(CloudPlayerMaps.serializer(), payload)
                rebuildMapCards(
                    catalogue.maps.map { entry ->
                        MapDisplayInfo(
                            id = entry.mapId,
                            name = entry.mapName,
                            subtitle = "Uploaded ${formatTimestamp(entry.uploadedAt)} - ${entry.sizeBytes} bytes",
                            status = "PENDING REVIEW",
                            isUserUpload = true,
                            iconClass = "fas fa-map",
                            author = AccelByteEnv.userId.take(8),
                            playerCount = "-",
                            territoryCount = "-",
                            schemaVersion = "-",
                            packSha = "-"
                        )
                    }
                )
                Logger.info(
                    LogCategory.UI,
                    "CollectionOverlay: refreshMapCatalogue() rebuilt ${catalogue.maps.size} cards"
                )
            }
            catch (e: Throwable)
            {
                Logger.error(LogCategory.UI, "CollectionOverlay: refreshMapCatalogue() threw: ${e.message}")
            }
        }
    }

    /**
     * Remove the card matching [mapId] from the maps tab without a server
     * round-trip. Used by the delete-success callback so the deleted row
     * disappears immediately — the operator does not have to close and
     * re-open the Collection overlay to scrub the entry. The follow-up
     * `refreshMapCatalogue()` reconciles against the server-side source of
     * truth (handles edge cases like the seed-upload probe path where the
     * catalogue was wiped before the delete RPC landed and the optimistic
     * removal fired against a stale snapshot).
     *
     * No-op when the catalogue is empty or the mapId is not present
     * (race-tolerant: the delete notification may arrive after a separate
     * refresh already removed the entry).
     */
    private fun removeMapCardById(mapId: String)
    {
        val target = mapCards.firstOrNull { entry ->
            entry.panel.getAttribute("data-map-id") == mapId
        } ?: run {
            Logger.info(LogCategory.UI, "CollectionOverlay: removeMapCardById mapId=$mapId not found in local cards (idempotent); skipping")
            return
        }
        mapsListContainer.remove(target.panel)
        mapCards.remove(target)
        noMapMatches.visible = mapCards.isEmpty()
        Logger.info(LogCategory.UI, "CollectionOverlay: removeMapCardById mapId=$mapId removed; remaining=${mapCards.size}")
    }

    /**
     * Rebuild the maps tab card list from the supplied entries. Empty list
     * surfaces the noMapMatches placeholder. The filter is re-applied so
     * the search box state stays consistent.
     */
    private fun rebuildMapCards(entries: List<MapDisplayInfo>)
    {
        mapCards.clear()
        mapsListContainer.removeAll()
        if (entries.isEmpty())
        {
            noMapMatches.visible = true
        }
        else
        {
            noMapMatches.visible = false
            // Mount the cards first, then build the renderers with
            // the cards' existing .co-thumb divs, then run the filter
            // pass. Order matters: the filter pass triggers a KVision
            // reactive style update on the card panel which would
            // overwrite any inline style the renderer had set. By
            // calling render() after applyMapFilter() the data URL
            // lands on the .co-thumb last and wins.
            entries.forEach { info ->
                val card = buildMapCard(info)
                mapsListContainer.add(card)
                val thumbDiv = (card.getElement()?.querySelector(".co-thumb") as? org.w3c.dom.HTMLDivElement)
                    ?: error("Map card ${info.id} is missing its .co-thumb div")
                val renderer = MapCardThumbnailRenderer(
                    mapInfo = info,
                    thumbDiv = thumbDiv,
                    fetcher = ::fetchMapPackBytes
                )
                mapCards.add(MapCardEntry(info.name.lowercase(), card, renderer))
            }
            applyMapFilter()
            // Fire render() AFTER applyMapFilter so the renderer's
            // setAttribute("style", ...) on the .co-thumb is the last
            // write to win the KVision reactive cascade.
            mapCards.forEach { it.thumbnailRenderer?.render() }
        }
        mapsListContainer.add(noMapMatches)
    }

    /**
     * Fetcher seam for [MapCardThumbnailRenderer]. Returns the raw pack
     * bytes returned by `server.extend.getPlayerMap`. The renderer's
     * own [unpackPackBlob] handles the JSZip unpack, which is the
     * single point at which the JS-side pack decode happens. (Earlier
     * revisions unpacked here AND in the renderer, which fed PNG bytes
     * into JSZip on the second pass — the operator-visible
     * "Can't find end of central directory" symptom.)
     */
    private suspend fun fetchMapPackBytes(mapId: String): ByteArray
    {
        val invoker = ServerExtendBridge.rpcInvoker
            ?: throw RuntimeException("rpcInvoker is null; cannot fetch map pack")
        val userId = globals.AccelByteEnv.userId
        val response = invoker.invoke(
            "server.extend.getPlayerMap",
            structs.rpcRequests.GetPlayerMapRequest(userId = userId, mapId = mapId),
            structs.rpcRequests.GetPlayerMapRequest.serializer()
        )
        if (response.error != null)
        {
            throw RuntimeException(
                response.error?.message ?: response.error?.code?.toString() ?: "Unknown RPC error"
            )
        }
        val payload = response.result
            ?: throw RuntimeException("getPlayerMap returned empty result")
        val resp = RpcJson.decodeFromJsonElement(
            structs.rpcRequests.GetPlayerMapResponse.serializer(),
            payload
        )
        // Bytes transport: pre-fix this RPC returned the 6.3 MB raw
        // pack base64-encoded into the JSON response — kotlinx.serialization's
        // ByteArray encoder was running synchronously on the server's
        // event-loop thread, blocking the SSE writer while it base64'd,
        // starving every other connected session of round-trip events.
        // Server-side `MapStorageProxy.getPlayerMap` now returns only the
        // presigned-URL string (the `mapPackBytes` field is empty).
        // Two paths from here:
        //   1. `resp.downloadUrl` is set → fetch it as `application/octet-stream`,
        //      assemble into an ArrayBuffer, copy into a Kotlin ByteArray.
        //   2. `resp.downloadUrl` is null (defensive: legacy server, AGS
        //      propagation lag, etc.) → fall back to the inline `mapPackBytes`
        //      field. The renderer keeps working.
        val downloadUrl = resp.downloadUrl
        if (downloadUrl != null && downloadUrl.isNotBlank())
        {
            return fetchBytesFromUrl(downloadUrl)
        }
        if (resp.mapPackBytes.isNotEmpty())
        {
            Logger.warn(
                LogCategory.UI,
                "CollectionOverlay: getPlayerMap returned inline mapPackBytes (size=${resp.mapPackBytes.size}); the server should switch to the downloadUrl path. Falling back for one render."
            )
            return resp.mapPackBytes
        }
        throw RuntimeException("getPlayerMap returned neither downloadUrl nor inline mapPackBytes for mapId=$mapId")
    }

    /**
     * Streams the raw map-pack bytes from a server-extend URL via the
     * standard `fetch().then(r => r.arrayBuffer())` pipeline. Returns
     * a Kotlin `ByteArray` the renderer can hand to `img.src =
     * blobUrl` for the asynchronous decode. The `arrayBuffer` path
     * (rather than `blob()`) avoids a copy inside the browser — the
     * `ByteArray` wrapper is an O(n) typed-array view.
     */
    private suspend fun fetchBytesFromUrl(url: String): ByteArray
    {
        // Why we run `fetch()` directly in a `js()` block: Kotlin/JS
        // cannot express the typed `Uint8Array` callback return type
        // and the `await` Promise chain interacts oddly with the
        // cast back to ByteArray. The `js()` wrapper lets us emit
        // native JS Promise + typed-array code without Kotlin/JS
        // trying to type-check it. The bridge: `fetch → arrayBuffer →
        // Uint8Array(view)`. The single-resolution Promise resolves
        // to a Uint8Array which we copy into a Kotlin ByteArray.
        val promise: kotlin.js.Promise<org.khronos.webgl.Uint8Array> = kotlin.js.Promise { resolve, reject ->
            try
            {
                js(
                    "fetch(url).then(function(r){return r.arrayBuffer();}).then(function(ab){resolve(new Uint8Array(ab));}).catch(function(e){reject(e);});"
                )
            }
            catch (e: Throwable)
            {
                reject(e)
            }
        }
        val u8 = promise.await()
        val bytes = ByteArray(u8.length)
        for (i in 0 until u8.length)
        {
            bytes[i] = (u8.asDynamic()[i] as Int).toByte()
        }
        return bytes
    }

    /**
     * Format an epoch-millis timestamp as a localized date + time string.
     * Uses the JS Date API directly (Kotlin/JS has no java.time).
     */
    private fun formatTimestamp(epochMillis: Long): String
    {
        val date = kotlin.js.Date(epochMillis.toDouble())
        return "${date.toLocaleDateString()} ${date.toLocaleTimeString()}"
    }

    override fun hide()
    {
        super.hide()
        dispositionDetailWindows()
        display = Display.NONE
        visible = false
    }

    private fun dispositionDetailWindows()
    {
        commanderDetailWindow.hide()
        storyDetailWindow.hide()
    }

    private fun buildSearchRow() = hPanel(
        className = "collection-search",
        spacing = 12,
        alignItems = AlignItems.CENTER
    ) {
        width = 100.perc
        icon("fas fa-search")
            searchField = textInput {
                width = 100.perc
                placeholder = "Search commanders..."
                onInput {
                    applyCommanderFilter()
                }
                setAttribute("spellcheck", "false")
            }
        }

    private fun buildMapsSearchRow() = hPanel(
        className = "collection-search",
        spacing = 12,
        alignItems = AlignItems.CENTER
    ) {
        width = 100.perc
        icon("fas fa-search")
        mapsSearchField = textInput {
            width = 100.perc
            placeholder = "Search maps by name, author, or tag…"
            onInput {
                applyMapFilter()
            }
            setAttribute("spellcheck", "false")
            setAttribute("data-testid", "maps-search-field")
        }
        button(
            "UPLOAD MAP",
            icon = "fas fa-cloud-arrow-up",
            className = "btn-upload-map"
        ) {
            setAttribute("data-testid", "maps-upload-button")
            setAttribute("title", "Upload a .map file from your computer")
            setAttribute("aria-label", "Upload a map file")
            onClick {
                mapUploadModal.show()
            }
        }
    }

    private fun applyMapFilter()
    {
        val filterValue = mapsSearchField.value?.trim()?.lowercase().orEmpty()
        var matchCount = 0
        mapCards.forEach { entry ->
            val matches = filterValue.isBlank() || entry.name.contains(filterValue)
            entry.panel.display = if(matches) Display.FLEX else Display.NONE
            entry.panel.visible = matches
            if(matches) matchCount++
        }
        noMapMatches.visible = matchCount == 0
        Logger.info(LogCategory.UI, "CollectionOverlay: maps-search-filter applied filter=" + filterValue + " match_count=" + matchCount)
    }

    /**
     * jsTest seam: returns a structural summary of the catalogue's
     * Maps tab so tests can assert on thumbnail-renderer wiring
     * without scraping the rendered DOM. Each entry is `name=<card
     * name>;thumbBackground=<.co-thumb background-image URL prefix or
     * empty>;iconPresent=<boolean>`. Pattern mirrors
     * [SimulationSettingsPage.renderForTest].
     */
    fun mapsTabSummaryForTest(): String
    {
        return mapCards.joinToString(separator = " | ") { entry ->
            val renderer = entry.thumbnailRenderer
            val bg = renderer?.thumbDiv?.style?.backgroundImage?.orEmpty() ?: ""
            val iconPresent = renderer?.thumbDiv?.querySelector("i") != null
            "name=${entry.name};thumbBackground=${bg.take(40)};iconPresent=$iconPresent"
        }
    }

    private fun rebuildCommanderCards()
    {
        commanderCards.clear()
        commanderListContainer.removeAll()
        val infos = collectCommanderInfos()
        infos.forEach { info ->
            val card = buildCommanderCard(info)
            commanderCards.add(CommanderCardEntry(info.name.lowercase(), card))
            commanderListContainer.add(card)
        }
        commanderListContainer.add(noCommanderMatches)
        applyCommanderFilter()
    }

    private fun applyCommanderFilter()
    {
        val filterValue = searchField.value?.trim()?.lowercase().orEmpty()
        var matchCount = 0
        commanderCards.forEach { entry ->
            val matches = filterValue.isBlank() || entry.name.contains(filterValue)
            entry.panel.display = if(matches) Display.FLEX else Display.NONE
            entry.panel.visible = matches
            if(matches)
            {
                matchCount++
            }
        }
        noCommanderMatches.visible = matchCount == 0
    }

    private fun buildCommanderCard(info: CommanderDisplayInfo) = hPanel(
        className = "collection-card",
        spacing = 18,
        alignItems = AlignItems.CENTER
    ) {
        width = 90.perc
        height = 120.px
        onClick {
            commanderDetailWindow.show(info)
        }

        icon(info.iconClass) {
            fontSize = 32.px
            color = Color.name(Col.CYAN)
        }

        vPanel(spacing = 6) {
            width = 100.perc
            h3(info.name) {
                color = Color.name(Col.WHITE)
                marginTop = 0.px
                marginBottom = 4.px
            }

            span("Type: ${info.type.name} • Trait: ${info.trait.name}") {
                color = Color.name(Col.LIGHTGRAY)
                fontSize = 16.px
            }
        }
    }

    private fun buildMapCard(info: MapDisplayInfo) = vPanel(
        className = "collection-card" + if(info.isUserUpload) " is-new" else "",
        spacing = 8
    ) {
        width = 100.perc
        onClick {
            Logger.info(LogCategory.UI, "CollectionOverlay: map-card click map_id=" + info.id + " map_status=" + info.status)
            mapDetailWindow.show(info)
        }
        setAttribute("data-testid", "map-card-${info.id}")
        setAttribute("data-map-id", info.id)
        setAttribute("data-map-status", info.status)

        // Thumbnail placeholder — the map editor's preview image will fill this
        // server-side once the upload RPC is wired (Task 19 hook).
        div(className = "co-thumb") {
            setAttribute("data-testid", "map-card-thumb-${info.id}")
            icon(info.iconClass)
        }

        h3(info.name) {
            color = Color.name(Col.WHITE)
            marginTop = 0.px
            marginBottom = 4.px
            fontSize = 13.px
        }

        span(info.subtitle) {
            color = Color.name(Col.LIGHTGRAY)
            fontSize = 11.px
        }

        // Status badge — STOCK (gray), PENDING REVIEW (gold), READY (green)
        add(buildStatusBadge(info))
    }

    private fun buildStatusBadge(info: MapDisplayInfo): io.kvision.html.Span {
        val badgeClass = when (info.status) {
            "PENDING REVIEW" -> "badge badge-pending"
            "READY" -> "badge badge-ready"
            else -> "badge badge-stock"
        }
        val s = span(info.status) {
            setAttribute("data-testid", "map-card-badge-${info.id}")
        }
        // Widget.className is internal, so we use addCssClass after construction
        s.addCssClass("badge")
        s.addCssClass(when (info.status) {
            "PENDING REVIEW" -> "badge-pending"
            "READY" -> "badge-ready"
            else -> "badge-stock"
        })
        return s
    }

    private fun renderStoryList()
    {
        storyListContainer.removeAll()
        storyEntries.forEach { story ->
            storyListContainer.add(buildStoryEntry(story))
        }
    }

    private fun buildStoryEntry(story: StoryEntry) = vPanel(
        className = "collection-story-entry",
        spacing = 6
    ) {
        width = 90.perc
        alignItems = AlignItems.START
        onClick {
            storyDetailWindow.show(story)
        }

        h3(story.title) {
            marginTop = 0.px
            marginBottom = 4.px
            color = Color.name(Col.WHITE)
        }

        span(story.dateCreated) {
            color = Color.name(Col.LIGHTBLUE)
            fontSize = 14.px
            marginBottom = 4.px
        }

        p(story.summary) {
            color = Color.name(Col.LIGHTGRAY)
            fontSize = 15.px
            marginBottom = 0.px
            lineHeight = 22.px
        }
    }

    private fun collectCommanderInfos() = World.availableCommanders.map { buildCommanderInfo(it) }

    private fun buildCommanderInfo(entry: Commander): CommanderDisplayInfo
    {
        val request = entry
        val nameValue = request.name.ifBlank { "Unnamed Commander" }
        val description = request.description.ifBlank { "No description has been recorded for this commander yet." }
        val empire = request.empire.ifBlank { "No empire notes were provided." }
        val type = request.type
        val trait = request.trait
        val iconClass = when(type)
        {
            CommanderType.Flying -> "fas fa-feather-alt"
            CommanderType.Aquatic -> "fas fa-water"
            CommanderType.Land -> "fas fa-mountain"
        }

        return CommanderDisplayInfo(
            name = nameValue,
            description = description,
            empire = empire,
            type = type,
            trait = trait,
            iconClass = iconClass,
            source = entry
        )
    }

    private fun switchPage(page: CollectionPage)
    {
        if(activePage == page)
        {
            return
        }

        val prior = activePage
        activePage = page
        when(page)
        {
            CollectionPage.COMMANDERS ->
            {
                commanderPage.display = Display.FLEX
                storyPage.display = Display.NONE
                mapsPage.display = Display.NONE
            }
            CollectionPage.STORIES ->
            {
                commanderPage.display = Display.NONE
                storyPage.display = Display.FLEX
                mapsPage.display = Display.NONE
            }
            CollectionPage.MAPS ->
            {
                commanderPage.display = Display.NONE
                storyPage.display = Display.NONE
                mapsPage.display = Display.FLEX
                Logger.info(LogCategory.UI, "CollectionOverlay: maps-tab switch from=" + prior + " to=MAPS")
            }
        }
        updateTabStyles()
    }

    private fun setActivePage(page: CollectionPage)
    {
        commanderPage.display = if(page == CollectionPage.COMMANDERS) Display.FLEX else Display.NONE
        storyPage.display = if(page == CollectionPage.STORIES) Display.FLEX else Display.NONE
        mapsPage.display = if(page == CollectionPage.MAPS) Display.FLEX else Display.NONE
        activePage = page
        updateTabStyles()
    }

    private fun updateTabStyles()
    {
        commanderTabButton.removeCssClass("collection-tab-button-active")
        storyTabButton.removeCssClass("collection-tab-button-active")
        mapsTabButton.removeCssClass("collection-tab-button-active")

        when(activePage)
        {
            CollectionPage.COMMANDERS -> commanderTabButton.addCssClass("collection-tab-button-active")
            CollectionPage.STORIES -> storyTabButton.addCssClass("collection-tab-button-active")
            CollectionPage.MAPS -> mapsTabButton.addCssClass("collection-tab-button-active")
        }
    }
}

private class CommanderDetailWindow : SimplePanel(className = "login-widget-window")
{
    private val commanderName = h3("Commander") {
        marginTop = 0.px
        marginBottom = 4.px
        color = Color.name(Col.WHITE)
        justifySelf = JustifyItems.CENTER
    }

    private val commanderMeta = span("Type / Trait") {
        color = Color.name(Col.LIGHTGRAY)
        fontSize = 16.px
        display = Display.BLOCK
        marginBottom = 8.px
        justifySelf = JustifyItems.CENTER
    }

    private val commanderDescriptionHeader = p("Commander Description") {
        color = Color.name(Col.LIGHTBLUE)
        fontSize = 18.px
        fontWeight = FontWeight.BOLD
        marginBottom = 4.px
        justifySelf = JustifyItems.CENTER
    }

    private val commanderDescription = p("Description") {
        color = Color.name(Col.WHITE)
        lineHeight = 22.px
        marginBottom = 10.px
        textAlign = TextAlign.JUSTIFY
        justifySelf = JustifyItems.CENTER
    }

    private val empireDescriptionHeader = p("Territory Notes") {
        color = Color.name(Col.LIGHTBLUE)
        fontSize = 18.px
        fontWeight = FontWeight.BOLD
        marginBottom = 4.px
        justifySelf = JustifyItems.CENTER
    }

    private val empireDescription = p("Empire Notes") {
        color = Color.name(Col.LIGHTGRAY)
        lineHeight = 20.px
        marginTop = 0.px
        textAlign = TextAlign.JUSTIFY
        justifySelf = JustifyItems.CENTER
    }

    init
    {
        width = 700.px
        height = 600.px
        position = Position.FIXED
        top = 50.perc
        left = 50.perc
        marginTop = (-300).px
        marginLeft = (-350).px
        zIndex = 9100
        padding = 25.px
        border = Border(2.px, BorderStyle.SOLID, Color("rgba(94, 106, 220, 0.5)"))
        background = Background(Color("rgba(8, 10, 24, 0.95)"))
        boxShadow = BoxShadow(0.px, 25.px, 50.px, color = Color("rgba(0, 0, 0, 0.85)"))
        flexDirection = FlexDirection.COLUMN
        display = Display.NONE

        add(h2("Commander Details") {
            marginTop = 0.px
            marginBottom = 12.px
            color = Color.name(Col.CYAN)
            textAlign = TextAlign.CENTER
        })

        vPanel {
            width = 100.perc
            flexGrow = 1
            overflow = Overflow.AUTO
            spacing = 12
            add(commanderName)
            add(commanderMeta)
            add(commanderDescriptionHeader)
            add(commanderDescription)
            add(empireDescriptionHeader)
            add(empireDescription)
        }

        hPanel(
            justify = JustifyContent.CENTER,
            alignItems = AlignItems.CENTER
        ) {
            width = 100.perc
            button("CLOSE", icon = "fas fa-times", className = "btn btn-secondary-action") {
                onClick {
                    this@CommanderDetailWindow.hide()
                }
            }
        }
    }

    fun show(info: CommanderDisplayInfo)
    {
        commanderName.content = info.name
        commanderMeta.content = "Type: ${info.type.name} • Trait: ${info.trait.name}"
        commanderDescription.content = info.description
        empireDescription.content = info.empire
        display = Display.FLEX
        visible = true
    }
}

private class StoryDetailWindow : SimplePanel(className = "login-widget-window")
{
    private val storyTitle = h3("Story Title") {
        marginTop = 0.px
        marginBottom = 6.px
        color = Color.name(Col.WHITE)
    }

    private val storyDate = span("Created") {
        color = Color.name(Col.LIGHTGRAY)
        fontSize = 14.px
        marginBottom = 10.px
    }

    private val storyBody = p("Story body") {
        color = Color.name(Col.LIGHTGRAY)
        lineHeight = 22.px
        textAlign = TextAlign.JUSTIFY
    }

    init
    {
        width = 700.px
        height = 650.px
        position = Position.FIXED
        top = 50.perc
        left = 50.perc
        marginTop = (-325).px
        marginLeft = (-350).px
        zIndex = 9100
        padding = 25.px
        border = Border(2.px, BorderStyle.SOLID, Color("rgba(94, 106, 220, 0.5)"))
        background = Background(Color("rgba(8, 10, 24, 0.95)"))
        boxShadow = BoxShadow(0.px, 25.px, 50.px, color = Color("rgba(0, 0, 0, 0.85)"))
        flexDirection = FlexDirection.COLUMN
        display = Display.NONE

        add(h2("Story Reader") {
            marginTop = 0.px
            marginBottom = 12.px
            color = Color.name(Col.CYAN)
        })

        vPanel {
            width = 100.perc
            flexGrow = 1
            overflow = Overflow.AUTO
            spacing = 0
            add(storyTitle)
            add(storyDate)
            add(storyBody)
        }

        hPanel(
            spacing = 12,
            justify = JustifyContent.CENTER,
            alignItems = AlignItems.CENTER
        ) {
            width = 100.perc
            button("Download .txt", icon = "fas fa-download", className = "btn btn-secondary-action") {
                onClick {
                    downloadCurrentStory()
                }
            }
            button("CLOSE", icon = "fas fa-times", className = "btn btn-secondary-action") {
                onClick {
                    this@StoryDetailWindow.hide()
                }
            }
        }
    }

    private var currentStory: StoryEntry? = null

    fun show(story: StoryEntry)
    {
        currentStory = story
        storyTitle.content = story.title
        storyDate.content = story.dateCreated
        storyBody.content = story.body + "\n\n[Story load is stubbed until backend playback is available.]"
        display = Display.FLEX
        visible = true
    }

    private fun downloadCurrentStory()
    {
        val story = currentStory ?: return
        val safeName = story.title.replace("\\s+".toRegex(), "-").lowercase()
        val blob = Blob(arrayOf(story.body), BlobPropertyBag(type = "text/plain;charset=utf-8"))
        val url = URL.createObjectURL(blob)
        val anchor = (document.createElement("a") as HTMLAnchorElement).apply {
            href = url
            download = "$safeName.txt"
        }

        document.body?.appendChild(anchor)
        anchor.click()
        document.body?.removeChild(anchor)
        URL.revokeObjectURL(url)
    }
}
